// includes/js-src/encounter-map/drag/difficult-terrain.ts
//
// Front-end mirror of includes/functions/janus/encounter/difficult_terrain.php.
// The drag "movement bar" colors the portion of a move that crosses difficult
// terrain, and the distance label reflects the doubled cost (each foot through
// difficult terrain costs 2 speed instead of 1 — movement halved). This is a
// PREVIEW only: the server (move_encounter_creature_logic.php) re-checks and is
// authoritative on drop. Keep the per-foot ×2 rule and the region sources in sync
// with the PHP helper.
//
// Region sources, both read off the encounter blob (the truth — the SAME data the
// authoritative move loop uses, so preview and cost agree by construction):
//   - OBJECTS: `difficult_terrain_rects` — exact get_object_rectangle footprints (feet),
//     precomputed server-side. FEET = client space (FEET_PER_GRID_UNIT = 1), no conversion.
//   - ZONES: `difficult_terrain_zone_keys` — status ids of difficult pAOE/spell zones; the
//     live geometry comes from the same XZPersistentAOELayer the map renders from
//     (resolveZonePlacement).
//
// Collision model: each foot-step tests the MOVING MODEL's footprint RECT (not just the path
// center point) against the regions — do_rectangles_overlap for objects, creature_intersects_aoe
// for zones — exactly as the move loop tests the caster rect per step. That makes it pure AREA
// collision: independent of movement type/direction, and the model's z-extent vs the region's
// z-extent is what decides elevation (a flyer at altitude won't collide with a ground region).
// The colored span tracks the model's shape, so it can extend slightly past the bare center line.

interface ZonePlacement {
    shapeKey?: unknown;
    originX?: unknown;
    originZ?: unknown;
    distX?: unknown;
    distZ?: unknown;
    center?: { x?: unknown; z?: unknown };
    aoeDistance?: unknown;
    aoeDistanceHeight?: unknown;
    aoeDistanceDepth?: unknown;
    [key: string]: unknown;
}

/** Axis-aligned XZ footprint in feet (x = left/right, z = bottom/top). */
export interface GridRect {
    left: number;
    right: number;
    bottom: number;
    top: number;
}

/** A difficult pAOE/spell zone, geometry from XZPersistentAOELayer.resolveZonePlacement (feet). */
export interface ZoneShape {
    shape: string; // 'sphere' | 'cylinder' | 'circle' | 'cube' | 'square' | 'line'
    originX: number;
    originZ: number;
    distX: number;
    distZ: number;
    centerX: number; // resolved WYSIWYG center for every centered shape
    centerZ: number;
    radius: number; // aoeDistance
    height: number; // aoeDistanceHeight (vertical slab for flat shapes)
    depth: number;  // aoeDistanceDepth (line half-width source)
}

/** The difficult regions on a map: object footprints + flagged zones. */
export interface DifficultRegions {
    rects: GridRect[];
    zones: ZoneShape[];
}

/** A contiguous run of the move line, flagged difficult or not. Endpoints in grid units. */
export interface DiffSegment {
    x1: number;
    z1: number;
    x2: number;
    z2: number;
    difficult: boolean;
}

export interface DiffSampleResult {
    /** Move line split into normal / difficult runs (grid-space endpoints). */
    segments: DiffSegment[];
    /** Total speed cost in feet — difficult feet counted twice. */
    spdCost: number;
    /** Raw distance in feet (rounded), ignoring terrain. */
    feet: number;
    /** Difficult portions as normalized [t0,t1] ranges along origin→target (for the WS payload). */
    fractions: Array<[number, number]>;
}

interface MinimalWidget {
    encounterArr?: Record<string, unknown>;
    _persistentAOELayer?: { resolveZonePlacement(statusId: string): ZonePlacement | null };
}

function num(v: unknown, d = 0): number {
    const n = Number(v);
    return Number.isFinite(n) ? n : d;
}

function recordOf(value: unknown): Record<string, unknown> | null {
    return value !== null && typeof value === 'object'
        ? value as Record<string, unknown>
        : null;
}

/**
 * Difficult regions for the widget's map, read off the encounter blob:
 *   - object footprint rects from `difficult_terrain_rects` (server-precomputed), and
 *   - difficult zones from `difficult_terrain_zone_keys` resolved to live geometry via the
 *     persistent-AOE layer the map already renders from.
 */
export function collectDifficultTerrainRegions(widget: MinimalWidget): DifficultRegions {
    const rects: GridRect[] = [];
    const rawRects = widget?.encounterArr?.difficult_terrain_rects;
    if (Array.isArray(rawRects)) {
        for (const r of rawRects) {
            const rect = recordOf(r);
            if (!rect) continue;
            const left = num(rect.left, NaN);
            const right = num(rect.right, NaN);
            const bottom = num(rect.bottom, NaN);
            const top = num(rect.top, NaN);
            if ([left, right, bottom, top].every(Number.isFinite)) {
                rects.push({ left, right, bottom, top });
            }
        }
    }

    const zones: ZoneShape[] = [];
    const rawKeys = widget?.encounterArr?.difficult_terrain_zone_keys;
    const layer = widget?._persistentAOELayer;
    if (Array.isArray(rawKeys) && layer && typeof layer.resolveZonePlacement === 'function') {
        for (const key of rawKeys) {
            const p = layer.resolveZonePlacement(String(key));
            if (!p) continue; // status gone, or keyed by status_connected_id (not in the status_id index)
            zones.push({
                shape: String(p.shapeKey || '').toLowerCase(),
                originX: num(p.originX),
                originZ: num(p.originZ),
                distX: num(p.distX, num(p.originX)),
                distZ: num(p.distZ, num(p.originZ)),
                centerX: num(p.center?.x, (num(p.originX) + num(p.distX, num(p.originX))) / 2),
                centerZ: num(p.center?.z, (num(p.originZ) + num(p.distZ, num(p.originZ))) / 2),
                radius: num(p.aoeDistance),
                height: num(p.aoeDistanceHeight),
                depth: num(p.aoeDistanceDepth),
            });
        }
    }

    return { rects, zones };
}

export function difficultRegionsEmpty(regions: DifficultRegions): boolean {
    return regions.rects.length === 0 && regions.zones.length === 0;
}

// ── Geometry mirrors of the PHP collision tests (rectangle_overlap_helpers.php +
//    persistent_aoe_geometry.php). The MOVING MODEL is a footprint RECT (not a point), so the
//    preview matches the authoritative per-step caster-rect collision the move loop runs. ──

/** do_rectangles_overlap: strict inequalities, so flush edges don't count. */
function rectsOverlap(a: GridRect, b: GridRect): boolean {
    return !(a.right <= b.left || a.left >= b.right || a.top <= b.bottom || a.bottom >= b.top);
}

function distSqPointToSegment(px: number, pz: number, ax: number, az: number, bx: number, bz: number): number {
    const abx = bx - ax, abz = bz - az;
    const len2 = abx * abx + abz * abz;
    if (len2 < 1e-14) {
        const dx = px - ax, dz = pz - az;
        return dx * dx + dz * dz;
    }
    let u = ((px - ax) * abx + (pz - az) * abz) / len2;
    u = Math.max(0, Math.min(1, u));
    const qx = ax + u * abx, qz = az + u * abz;
    const dx = px - qx, dz = pz - qz;
    return dx * dx + dz * dz;
}

function distSqPointToAabb(px: number, pz: number, l: number, r: number, b: number, t: number): number {
    const cx = Math.max(l, Math.min(px, r));
    const cz = Math.max(b, Math.min(pz, t));
    const dx = px - cx, dz = pz - cz;
    return dx * dx + dz * dz;
}

/** creature_footprint_intersects_line_aoe_segment: thick segment vs footprint rect. */
function rectIntersectsLine(rect: GridRect, ox: number, oz: number, ex: number, ez: number, halfWidth: number): boolean {
    const w2 = halfWidth * halfWidth + 1e-12;
    const corners: Array<[number, number]> = [
        [rect.left, rect.bottom], [rect.right, rect.bottom], [rect.right, rect.top], [rect.left, rect.top],
    ];
    for (const c of corners) {
        if (distSqPointToSegment(c[0], c[1], ox, oz, ex, ez) <= w2) return true;
    }
    const segLen = Math.hypot(ex - ox, ez - oz);
    const steps = Math.max(16, Math.min(200, Math.ceil(segLen * 24)));
    for (let i = 0; i <= steps; i++) {
        const s = steps > 0 ? i / steps : 0;
        const px = ox + (ex - ox) * s, pz = oz + (ez - oz) * s;
        if (distSqPointToAabb(px, pz, rect.left, rect.right, rect.bottom, rect.top) <= w2) return true;
    }
    return false;
}

/** Footprint-rect vs zone test mirroring PHP creature_intersects_aoe (which takes the caster rect). */
function rectInZone(rect: GridRect, z: ZoneShape): boolean {
    const halfV = z.height / 2;
    switch (z.shape) {
        case 'sphere':
        case 'cylinder': {
            const cx = Math.max(rect.left, Math.min(z.centerX, rect.right));
            const cz = Math.max(rect.bottom, Math.min(z.centerZ, rect.top));
            const dx = z.centerX - cx, dz = z.centerZ - cz;
            return dx * dx + dz * dz <= z.radius * z.radius;
        }
        case 'circle': {
            const l = z.centerX - z.radius, r = z.centerX + z.radius;
            const b = z.centerZ - halfV, t = z.centerZ + halfV;
            return !(rect.right < l || rect.left > r || rect.top < b || rect.bottom > t);
        }
        case 'cube':
        case 'square': {
            const cx = z.centerX, cz = z.centerZ;
            const half = z.radius / 2;
            const halfVert = z.shape === 'square' ? halfV : half;
            const l = cx - half, r = cx + half, b = cz - halfVert, t = cz + halfVert;
            return !(rect.right < l || rect.left > r || rect.top < b || rect.bottom > t);
        }
        case 'line': {
            // HEIGHT, not depth — X is the line's length axis and Z is elevation in this side view, so
            // the perpendicular extent is the height. Depth is the into-screen thickness (3D only).
            // Matches janus_line_aoe_half_width_feet() so a difficult-terrain line costs movement over
            // the same band Janus damages.
            let hw = 2.5;
            if (z.height > 0) hw = Math.max(2.5, z.height / 2);
            return rectIntersectsLine(rect, z.originX, z.originZ, z.distX, z.distZ, hw);
        }
        default:
            return false;
    }
}

function rectIsDifficult(rect: GridRect, regions: DifficultRegions): boolean {
    for (const r of regions.rects) {
        if (rectsOverlap(rect, r)) return true;
    }
    for (const z of regions.zones) {
        if (rectInZone(rect, z)) return true;
    }
    return false;
}

/**
 * Split origin→target (grid coords) into normal / difficult runs by stepping the MODEL along
 * the path 1 ft at a time and classifying each foot by whether the model's footprint RECT
 * (centered on that foot, half-extents `halfX`/`halfZ` in feet) collides with any region —
 * the SAME per-step caster-rect collision the authoritative move loop runs (objects via
 * do_rectangles_overlap, zones via creature_intersects_aoe). So the colored span tracks the
 * model's shape, not just the center line.
 */
export function sampleDifficultSegments(
    origin: { x: number; z: number },
    target: { x: number; z: number },
    regions: DifficultRegions,
    feetPerGrid: number,
    halfX: number,
    halfZ: number,
): DiffSampleResult {
    const dxg = target.x - origin.x;
    const dzg = target.z - origin.z;
    const feet = Math.round(Math.hypot(dxg, dzg) * feetPerGrid);

    if (feet <= 0) {
        return { segments: [], spdCost: 0, feet: 0, fractions: [] };
    }
    if (difficultRegionsEmpty(regions)) {
        return {
            segments: [{ x1: origin.x, z1: origin.z, x2: target.x, z2: target.z, difficult: false }],
            spdCost: feet,
            feet,
            fractions: [],
        };
    }

    const n = feet;
    const classes: boolean[] = new Array(n);
    for (let i = 0; i < n; i++) {
        const tm = (i + 0.5) / n;
        const px = origin.x + dxg * tm, pz = origin.z + dzg * tm;
        classes[i] = rectIsDifficult(
            { left: px - halfX, right: px + halfX, bottom: pz - halfZ, top: pz + halfZ },
            regions,
        );
    }

    const segments: DiffSegment[] = [];
    const fractions: Array<[number, number]> = [];
    let spdCost = 0;
    let segStart = 0;
    for (let i = 0; i < n; i++) {
        const difficult = classes[i] ?? false;
        spdCost += difficult ? 2 : 1;
        const boundary = i + 1 >= n || classes[i + 1] !== difficult;
        if (boundary) {
            const t0 = segStart / n;
            const t1 = (i + 1) / n;
            segments.push({
                x1: origin.x + dxg * t0,
                z1: origin.z + dzg * t0,
                x2: origin.x + dxg * t1,
                z2: origin.z + dzg * t1,
                difficult,
            });
            if (difficult) fractions.push([t0, t1]);
            segStart = i + 1;
        }
    }

    return { segments, spdCost, feet, fractions };
}
